import { FC, ReactNode, useState } from "react";

import { Stack } from "@mui/material";

import { useAssistantConfig } from "../../../contexts/AssistantConfigContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardBaseInput } from "@/dashboard-design-system/BaseInput/DashboardBaseInput";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

const NOTE_MAX_LENGTH = 2000;

interface ApprovalCardProps {
  description: string;
  details?: ReactNode;
  noteLabel?: string;
  approveLabel?: string;
  approveDisabled?: boolean;
  onApprove: (note?: string) => void;
  onDecline: () => void;
}

export const ApprovalCard: FC<ApprovalCardProps> = ({
  description,
  details,
  noteLabel,
  approveLabel,
  approveDisabled,
  onApprove,
  onDecline,
}) => {
  const { t } = useTranslate();
  const { feature } = useAssistantConfig();
  const { question, consequence } = splitQuestion(description);
  const [note, setNote] = useState("");
  return (
    <Stack
      spacing={2}
      sx={(theme) => ({
        p: 2,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: theme.radius(1),
        bgcolor: theme.palette.surface.neutral.white,
      })}
      data-test={`${feature}-approval-card`}
    >
      <DashboardTypography variant="subtitle2" vibe="text-neutral-intense" sx={{ whiteSpace: "pre-wrap" }}>
        {question}
        {consequence && (
          <DashboardTypography component="span" variant="body2" vibe="text-neutral-intense">
            {` ${consequence}`}
          </DashboardTypography>
        )}
      </DashboardTypography>
      {details}
      {noteLabel && (
        <DashboardBaseInput
          multiline
          minRows={2}
          maxRows={6}
          value={note}
          placeholder={noteLabel}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{
            "aria-label": noteLabel,
            "data-test": `${feature}-approval-note`,
            maxLength: NOTE_MAX_LENGTH,
          }}
        />
      )}
      <Stack direction="row" spacing={1}>
        <DashboardButton
          variant="filled"
          size="small"
          fullWidth
          disabled={approveDisabled}
          onClick={() => onApprove(note.trim() || undefined)}
        >
          {approveLabel ?? t("dashboard", "assistant.approval.approve")}
        </DashboardButton>
        <DashboardButton variant="outlined" size="small" sx={{ flexShrink: 0 }} onClick={onDecline}>
          {t("dashboard", "assistant.approval.decline")}
        </DashboardButton>
      </Stack>
    </Stack>
  );
};

const splitQuestion = (description: string): { question: string; consequence: string | null } => {
  const match = /^(.+?[?.!])\s+(.+)$/s.exec(description);
  return match ? { question: match[1], consequence: match[2] } : { question: description, consequence: null };
};
